import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { PickupOption, Restaurant, CustomerProfile } from '@/models';
import { ErrorEvent, FindAChipotleClickEvent } from '@/services/analytics/events';

const PHONE_NUMBER_CALLING_CODE_REGEX = /^\+\d+/g;

interface PickupType {
  displayName: string;
  analyticsName: string;
}

@Component({
  name: 'CmgFindAChipotleRestaurantDetails',
})
export default class CmgFindAChipotleRestaurantDetails extends Vue {
  @Prop({ required: true, default: false, type: Object })
  restaurant!: Restaurant;
  pickupButtonLoading = false;
  heartHover = false;

  get closeButtonImageUrl(): string {
    return this.$appStore.state.cms.images.buttons.xDarkBrown.default;
  }

  get directionsIconUrl(): string {
    return this.$appStore.state.cms.images.icons.directions.default;
  }

  get directionsUrl(): string | undefined {
    const address = this.restaurant.address;
    return this.$services.googleMaps.getDirectionsUrl(address.latitude, address.longitude);
  }

  get isDigitalKitchen(): boolean {
    return this.restaurant.digitalKitchen;
  }

  get infoIconUrl(): string {
    return this.$appStore.state.cms.images.icons.infoOrangeBrown.default
  }

  get isDiningRoomOpen(): boolean {
    return this.restaurant.diningRoomOpen;
  }

  get pickupTypes(): PickupType[] {
    let pickupTypes = [] as PickupType[];
    
    if (this.restaurant.pickupInsideEnabled) {
      pickupTypes.push({ displayName: PickupOption.Pickup, analyticsName: 'pick-up' });
    }
    if (this.restaurant.walkupWindowEnabled) {
      pickupTypes.push({ displayName: PickupOption.WalkUp, analyticsName: 'walk-up' });
    }
    if (this.restaurant.chipotlanePickupEnabled) {
      pickupTypes.push({ displayName: PickupOption.Chipotlane, analyticsName: 'chipotlane' });
    }
    if (this.restaurant.digitalKitchen) {
      pickupTypes.push({ displayName: PickupOption.DigitalKitchen, analyticsName: 'digital-kitchen' });
    }
    
    return pickupTypes;
  }

  get isLoggedIn(): boolean | undefined {
    return this.$services.customer.isLoggedIn();
  }

  landmark(): string | undefined {
    if (!this.restaurant.directions) {
      return '';
    }

    const directions = this.restaurant.directions;

    return directions.landmark
      ? directions.landmark
      : directions.crossStreet1 && directions.crossStreet2
      ? `Near ${directions.crossStreet1} and ${directions.crossStreet2}`
      : '';
  }

  get addressLine1(): string {
    return this.restaurant.address.addressLine1 || '';
  }

  get addressLine2(): string {
    const address = this.restaurant.address;
    const postalCodeSplit = address.postalCode ? address.postalCode.split('-') : [];
    const postalCode = postalCodeSplit.length > 0 ? postalCodeSplit[0] : address.postalCode;
    return `${address.locality}, ${address.administrativeArea ? address.administrativeArea + ' ' : ''}${postalCode}`;
  }

  get hours(): string[] {
    return this.$services.restaurant.getFormattedRestaurantHours(this.restaurant);
  }

  get isPickupDisabled(): boolean {
    return !this.restaurant.onlineOrderingEnabled || !this.$services.restaurant.isRestaurantOpenForPickup(this.restaurant);
  }

  get isRestaurantClosed(): boolean {
    return !this.$services.restaurant.isRestaurantOpen(this.restaurant);
  }

  get pickupButtonBackground(): string {
    return this.isPickupDisabled ? 'grey-pinkish' : 'brown';
  }

  get isCateringDisabled(): boolean {
    return !this.restaurant.cateringEnabled;
  }

  get cateringButtonColor(): string {
    return this.isCateringDisabled ? 'grey-pinkish' : 'brown';
  }

  get favoriteRestaurantName(): string | undefined {
    const favoriteRestaurantName = this.$services.customer.getFavoriteRestaurantName(this.restaurant.restaurantNumber);
    return favoriteRestaurantName ?? undefined;
  }

  get customer(): CustomerProfile | null {
    const profile = this.$appStore.state.customer.profile;
    return profile && !profile.isGuest ? profile : null;
  }

  mounted() {
    Vue.nextTick(() => {
      setTimeout(() => {
        const focusValidaddress = document.getElementsByClassName('detailsFav')[0] as HTMLElement;
        if (focusValidaddress) {
          focusValidaddress.focus();
        }
      }, 2000);
    });
    document.body.addEventListener('keyup', this.closeModalOnEscapeEventHandler);

    if (!this.restaurant.onlineOrderingEnabled) {
      this.$services.analytics.send(new ErrorEvent('locator', 'Online ordering unavailable.', 'restaurant-details'));
    }
  }
  
  async onPickupClick() {
    this.pickupButtonLoading = true;
    this.$services.order.setDeliveryAddress(null);
    this.$services.order.setCachedRestaurant(this.restaurant);
    this.$emit('order-from-restaurant', this.restaurant);
  }

  onOrderCateringClick() {
    this.$services.analytics.send(new FindAChipotleClickEvent('cateringCTA'));
    window.open(this.$services.config.getCateringUrl(), '_blank');
  }

  getHeartIcon(): string {
    return this.$services.customer.isFavoriteRestaurant(this.restaurant.restaurantNumber)
      ? this.$appStore.state.cms.images.icons.heartFilled.default
      : this.heartHover
      ? this.$appStore.state.cms.images.icons.heartHovered.default
      : this.$appStore.state.cms.images.icons.heartEmpty.default;
  }

  toggleFavorite(event: any) {
    this.$services.ui.pushEventTargetAsLastSelectedElement(event);
    if (this.$services.customer.isFavoriteRestaurant(this.restaurant.restaurantNumber)) {
      this.$services.customer.removeFavoriteRestaurant(this.restaurant.restaurantNumber);
      (this.$refs.heart as HTMLElement).dispatchEvent(new Event('mouseleave'));
    } else {
      this.$services.analytics.send(new FindAChipotleClickEvent('restaurantFavorited', this.restaurant.restaurantNumber));
      this.$services.ui.setModalVisibility('favoriteRestaurant', true, { restaurantNumber: this.restaurant.restaurantNumber });
      this.$services.ui.firstFocus();
    }
  }

  onClose() {
    this.$emit('close');
    this.$services.ui.nextFocus('nearby-text');
  }

  onClickPickupOption(event: any): void {
    this.$services.ui.pushEventTargetAsLastSelectedElement(event);
    this.$services.ui.setModalVisibility('pickupOptionsModal', true, {
      restaurant: this.restaurant,
    });
    this.$services.ui.firstFocus();
  }

  private closeModalOnEscapeEventHandler(event: KeyboardEvent) {
    if (event.key == 'Escape') {
      if (this.$appStore.state.ui.modals.bagReconciliation.visible) {
        this.$services.ui.setModalVisibility('bagReconciliation', false);
        const focusNearby = document.getElementsByClassName('address-details-close')[0] as HTMLElement;
        if (focusNearby) {
          focusNearby.focus();
        }
      } else {
        const selection = document.querySelector('.cmg-favorite-restaurant-modal') !== null;
        if (selection && document.querySelectorAll('.cmg-favorite-restaurant-modal')[0].childElementCount > 0) {
          this.$services.ui.setModalVisibility('favoriteRestaurant', false);
          this.$services.ui.focusBack();
        } else {
          this.$emit('close');

          Vue.nextTick(() => {
            setTimeout(() => {
              const focusNearbyRecent = document.getElementsByClassName('most-recent-title-txt')[0] as HTMLElement;
              const focusNearbyFav = document.getElementsByClassName('favorite-address-txt')[0] as HTMLElement;
              const focusNearby = document.getElementsByClassName('nearby-text')[0] as HTMLElement;
              if (focusNearbyRecent) {
                focusNearbyRecent.focus();
              } else if (focusNearbyFav) {
                focusNearbyFav.focus();
              } else {
                focusNearby.focus();
              }
            });
          });
        }
      }
    }
  }
}
